import React, { useState, useEffect, useRef, useContext } from "react";
import { ShoppingCart, Menu, MessageCircle } from "lucide-react"; 
import SideMenu from "./SideMenu";
import SignInModal from "./SignInModal";
import CreateAccountModal from "./CreateAccountModal";
import HelpModal from "./HelpModal";
import { CartContext } from "../context/CartContext";
import CartDrawer from "./CartDrawer";
import { menuData } from "../data/menuData";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Floating Chat AI popup
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const dropdownRef = useRef(null);

  // Cart Context
  const {
    cart = [],
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
  } = useContext(CartContext);

  const cartCount = Array.isArray(cart)
    ? cart.reduce((sum, item) => sum + (item.quantity || 1), 0)
    : 0;

  // Close sign-in dropdown outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search handler
  const handleSearch = (term) => {
    setSearchTerm(term);

    if (term.trim() === "") {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = [];

    Object.entries(menuData).forEach(([key, menu]) => {
      if (menu.items) {
        menu.items.forEach((item) => {
          if (item.name.toLowerCase().includes(term.toLowerCase())) {
            results.push({ ...item, category: menu.title });
          }
        });
      }

      if (menu.sections) {
        menu.sections.forEach((section) => {
          section.items.forEach((item) => {
            if (item.name.toLowerCase().includes(term.toLowerCase())) {
              results.push({ ...item, category: section.title });
            }
          });
        });
      }
    });

    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Show popup when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setShowHelpPopup(true);
      } else {
        setShowHelpPopup(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Sticky Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-gray-900 text-white py-3 shadow-md">
        <div className="max-w-screen-xl mx-auto flex justify-between items-center px-6">
          {/* Left: Menu + Logo */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setMenuOpen(true)}
              className="text-white hover:text-amber-400"
            >
              <Menu size={26} />
            </button>
            <h1 className="text-2xl font-bold text-amber-500 tracking-wide cursor-pointer">
              TheTropicalShop
            </h1>
          </div>

          {/* Search Bar (Desktop) */}
          <div className="relative hidden md:flex items-center w-1/2">
            <input
              type="text"
              placeholder="Search for products, brands and more..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full p-2 text-gray-800 rounded-l-md outline-none"
            />
            <button
              className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-r-md"
              onClick={() => handleSearch(searchTerm)}
            >
              Search
            </button>

            {/* Search Results */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 w-full bg-white shadow-lg rounded-md mt-1 max-h-60 overflow-y-auto z-50">
                {searchResults.map((result, idx) => (
                  <div
                    key={idx}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-800"
                    onClick={() => {
                      alert(`Opening: ${result.name}`);
                      setSearchTerm("");
                      setShowSearchResults(false);
                    }}
                  >
                    <p className="font-medium">{result.name}</p>
                    <p className="text-xs text-gray-500">{result.category}</p>
                  </div>
                ))}
              </div>
            )}

            {/* No Results */}
            {showSearchResults && searchResults.length === 0 && (
              <div className="absolute top-full left-0 w-full bg-white shadow-lg rounded-md mt-1 p-3 text-gray-500 text-sm">
                No results found for "{searchTerm}"
              </div>
            )}
          </div>

          {/* Right Navigation */}
          <div className="flex items-center space-x-6 relative">
            {/* <a href="#" className="hover:text-amber-400">
              Chat with AI
            </a> */}

            <button
              onClick={() => setShowHelp(true)}
              className="hover:text-amber-400"
            >
              Help
            </button>

            {/* Sign In */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md font-semibold transition"
              >
                Sign In
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white text-gray-800 rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-4 border-b">
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        setShowSignIn(true);
                      }}
                      className="w-full bg-amber-500 text-white py-2 rounded-md font-semibold hover:bg-amber-600"
                    >
                      Sign In
                    </button>
                    <p className="text-center text-sm text-gray-600 mt-2">
                      New customer?{" "}
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          setShowCreate(true);
                        }}
                        className="text-amber-500 hover:underline"
                      >
                        Create account
                      </button>
                    </p>
                  </div>

                  <div className="p-4">
                    <h4 className="text-sm font-bold mb-2">Your Account</h4>
                    <ul className="space-y-1 text-sm">
                      <li className="hover:text-amber-600 cursor-pointer">
                        Your Orders
                      </li>
                      <li className="hover:text-amber-600 cursor-pointer">
                        Wishlist
                      </li>
                      <li className="hover:text-amber-600 cursor-pointer">
                        Rewards & Coupons
                      </li>
                      <li className="hover:text-amber-600 cursor-pointer">
                        Recently Viewed
                      </li>
                      <li className="hover:text-amber-600 cursor-pointer">
                        Customer Support
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Cart */}
            <button
              className="relative hover:text-amber-400"
              onClick={() => setShowCart(true)}
            >
              <ShoppingCart size={26} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-amber-500 text-xs rounded-full px-1.5 py-0.5">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Side Menu */}
      {menuOpen && <SideMenu onClose={() => setMenuOpen(false)} />}

      {/* Cart Drawer */}
      {showCart && <CartDrawer onClose={() => setShowCart(false)} />}

      {/* Modals */}
      {showSignIn && (
        <SignInModal
          onClose={() => setShowSignIn(false)}
          onSwitch={(view) => {
            setShowSignIn(false);
            if (view === "create") setShowCreate(true);
          }}
        />
      )}

      {showCreate && (
        <CreateAccountModal
          onClose={() => setShowCreate(false)}
          onSwitch={(view) => {
            setShowCreate(false);
            if (view === "signin") setShowSignIn(true);
          }}
        />
      )}

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {/* Floating Chat AI Button */}
      <div className="fixed bottom-5 right-5 z-50">
        <button className="bg-amber-500 text-white p-4 rounded-full shadow-xl hover:bg-amber-600 transition">
          <MessageCircle size={30} />
        </button>
      </div>

      {/* Auto Popup */}
      {showHelpPopup && (
        <div className="fixed bottom-24 right-6 bg-white shadow-xl border px-4 py-2 rounded-xl text-gray-800 text-sm z-50 animate-popup">
          👋 Hi! Need any help?
        </div>
      )}

      {/* Popup animation */}
      <style>
        {`
          @keyframes popup {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-popup {
            animation: popup 0.35s ease-out;
          }
        `}
      </style>

      <div className="pt-20"></div>
    </>
  );
};

export default Header;
